<template>
    <div>
        <fieldset>
            <legend class="text-blue">
                {{ $t("journalEntry.itemDetail") }}
                <Tooltip placement="top">
                    <Icon type="ios-information-circle-outline" size="17" />
                    <div slot="content">
                        <div class="tw-text-xs">
                            Journal entry item details.
                        </div>
                    </div>
                </Tooltip>
            </legend>
            <div class="overflow-auto tw-max-h-40" style="min-height: 100px;">
                <div class="table-ui-detail">
                    <table>
                        <thead>
                            <tr>
                                <th width="8%">
                                    {{ $t("journalEntry.accountNumber") }}
                                </th>
                                <th width="19%">
                                    {{ $t("journalEntry.accountName") }}
                                </th>
                                <th class="text-center" width="5%">
                                    {{ $t("journalEntry.currencyCode") }}
                                </th>
                                <th class="text-center" width="5%">
                                    {{ $t("journalEntry.displayOrder") }}
                                </th>
                                <th class="text-center" width="15%">
                                    {{ $t("journalEntry.debitAmount") }}
                                </th>
                                <th class="text-center" width="15%">
                                    {{ $t("journalEntry.creditAmount") }}
                                </th>
                                <th>
                                    {{ $t("journalEntry.remarks") }}
                                </th>
                                <th class="text-center" width="5%">
                                    {{ $t("actions") }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="items.length == 0">
                                <td
                                    colspan="8"
                                    class="tw-text-center tw-p-3 tw-space-x-1"
                                >
                                    <i
                                        class="fa fa-folder-open tw-text-gray-200"
                                    ></i>
                                    <label class="tw-text-gray-400">
                                        {{ $t("journalEntry.noData") }}
                                    </label>
                                    <div
                                        class="validate tw-text-left"
                                        v-if="errors.has('details')"
                                    >
                                        {{ errors.first("details") }}
                                    </div>
                                </td>
                            </tr>
                            <tr
                                v-for="(detail, index) in items"
                                :key="index"
                            >
                                <td class="tw-whitespace-nowrap">
                                    {{ detail.account_number }}
                                </td>
                                <td class="tw-whitespace-nowrap">
                                    {{ detail.account_name_en }}
                                </td>
                                <td class="text-center tw-whitespace-nowrap">
                                    {{ detail.currency_code }}
                                </td>
                                <td class="text-center tw-whitespace-nowrap">
                                    <input
                                        type="number"
                                        v-model.number="detail.display_order"
                                        class="form-control form-control-sm text-center"
                                    />
                                </td>
                                <td class="text-center">
                                    <Poptip trigger="focus">
                                        <Input
                                            type="number"
                                            v-model.number="detail.debit_amount"
                                            @on-change="
                                                () =>
                                                    onChangeDebitAmount(detail)
                                            "
                                            :placeholder="
                                                $t('journalEntry.enterNumber')
                                            "
                                            style="width: 100%"
                                            :class="{
                                                'ivu-form-item-error': errors.has(
                                                    'details.' +
                                                        index +
                                                        '.debit_amount'
                                                )
                                            }"
                                            class="tw-text-right"
                                        >
                                            <i class="fas" slot="prefix">{{
                                                getCurrencySign(
                                                    model.currency_id
                                                )
                                            }}</i>
                                        </Input>
                                        <div slot="content">
                                            <ts-display-currency
                                                v-if="detail.debit_amount"
                                                :amount="detail.debit_amount"
                                                :currency_id="model.currency_id"
                                                style="width:100%"
                                            />
                                            <span v-else>{{
                                                $t("journalEntry.enterNumber")
                                            }}</span>
                                        </div>
                                    </Poptip>
                                </td>
                                <td class="text-center">
                                    <Poptip trigger="focus">
                                        <Input
                                            type="number"
                                            v-model.number="
                                                detail.credit_amount
                                            "
                                            @on-change="
                                                () =>
                                                    onChangeCreditAmount(detail)
                                            "
                                            :placeholder="
                                                $t('journalEntry.enterNumber')
                                            "
                                            style="width: 100%"
                                            :class="{
                                                'ivu-form-item-error': errors.has(
                                                    'details.' +
                                                        index +
                                                        '.credit_amount'
                                                )
                                            }"
                                            class="tw-text-right"
                                        >
                                            <i class="fas" slot="prefix">{{
                                                getCurrencySign(
                                                    model.currency_id
                                                )
                                            }}</i>
                                        </Input>
                                        <div slot="content">
                                            <ts-display-currency
                                                v-if="detail.credit_amount"
                                                :amount="detail.credit_amount"
                                                :currency_id="model.currency_id"
                                                style="width:100%"
                                            />
                                            <span v-else>{{
                                                $t("journalEntry.enterNumber")
                                            }}</span>
                                        </div>
                                    </Poptip>
                                </td>
                                <td class="text-center">
                                    <input
                                        v-model="detail.remarks"
                                        class="form-control form-control-sm"
                                        :class="{
                                            'is-invalid': errors.has(
                                                'details.' + index + '.remarks'
                                            )
                                        }"
                                    />
                                    <div
                                        class="invalid-feedback"
                                        v-if="
                                            errors.has(
                                                'details.' + index + '.remarks'
                                            )
                                        "
                                    >
                                        {{
                                            errors.first(
                                                "details." + index + ".remarks"
                                            )
                                        }}
                                    </div>
                                </td>
                                <td class="text-center">
                                    <Tooltip
                                        placement="top"
                                        :transfer="true"
                                        :content="$t('remove')"
                                    >
                                        <a
                                            href="#"
                                            class="text-danger"
                                            @click.prevent="removeRow(detail)"
                                        >
                                            <Icon
                                                type="ios-remove-circle-outline"
                                                size="20"
                                            />
                                        </a>
                                    </Tooltip>
                                </td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr
                                style="background-color: #e3e8f1;"
                                class="tw-text-base"
                            >
                                <td class="tw-whitespace-nowrap" colspan="2">
                                    <b
                                        >{{ $t("journalEntry.row") }} :
                                        {{ model.details.length }}</b
                                    >
                                </td>
                                <td
                                    class="tw-whitespace-nowrap tw-text-right"
                                    colspan="2"
                                >
                                    <b>{{ $t("journalEntry.total") }} :</b>
                                </td>
                                <td class="tw-whitespace-nowrap tw-text-center">
                                    <ts-display-currency
                                        :amount="totalDebit"
                                        :currency_id="model.currency_id"
                                        style="width:100%;padding: 0 7px 0 12px;"
                                    />
                                </td>
                                <td class="tw-whitespace-nowrap tw-text-center">
                                    <ts-display-currency
                                        :amount="totalCredit"
                                        :currency_id="model.currency_id"
                                        style="width:100%;padding: 0 7px 0 12px;"
                                    />
                                </td>
                                <td
                                    colspan="2"
                                    class="tw-text-center tw-text-xs text-danger"
                                >
                                    <small
                                        class="validate text-danger"
                                        v-if="errors.has('invalid_total')"
                                    >
                                        {{ errors.first("invalid_total") }}
                                    </small>
                                </td>
                            </tr>
                        </tfoot>
                    </table>
                </div>
            </div>
        </fieldset>
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import { mapActions, mapState } from "vuex";
import { orderBy } from "lodash";

export default {
    name: "itemDetails",
    props: ["value", "validate"],
    data() {
        return {
            errors: new Errors(),
            model: {}
        };
    },
    created() {
        this.model = this.value;
    },
    computed: {
        ...mapState("accounting/journalEntry", ["formViewDatas", "edit_data"]),
        totalDebit() {
            if (this.model.details.length > 0) {
                return this.model.details.reduce(
                    (n, { debit_amount }) => n + debit_amount,
                    0
                );
            }
            return 0;
        },
        totalCredit() {
            if (this.model.details.length > 0) {
                return this.model.details.reduce(
                    (n, { credit_amount }) => n + credit_amount,
                    0
                );
            }
            return 0;
        },
        items() {
            return orderBy(this.model.details, ['display_order']);
        }
    },
    methods: {
        ...mapActions("accounting/journalEntry", ["getFormViewModel"]),
        removeRow(item) {
            this.model.details.splice(this.model.details.indexOf(item), 1);
        },
        onChangeDebitAmount(detail) {
            detail.credit_amount = null;
        },
        onChangeCreditAmount(detail) {
            detail.debit_amount = null;
        },
        getCurrencySign(currency_id) {
            if (this.formViewDatas["currency"]) {
                const currency = this.formViewDatas["currency"].find(
                    el => el.currency_id == currency_id
                );

                return currency ? currency.sign : null;
            }
            return null;
        },
        getPrecision(currency_id) {
            if (!this.formViewDatas["currency"]) {
                const currency = this.formViewDatas["currency"].find(
                    el => el.currency_id == currency_id
                );

                return currency ? currency.currency_rounding : null;
            }
            return null;
        }
    },
    watch: {
        validate: function(error) {
            this.errors = new Errors(error.errors);
        }
    }
};
</script>
<style>
.ant-calendar-picker-input {
    height: 32px !important;
}
.ivu-input-prefix i,
.ivu-input-suffix i {
    font-family: Consolas, Hanuman, Courier, monospace;
}
.table-ui-detail .ivu-input {
    text-align: right;
}
</style>
<style scoped>
.form-control {
    height: 32px;
}
</style>
